import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

const SENTRY_ISSUES_URL = 'https://color-and-light.sentry.io/issues/';
const SENTRY_LOGS_URL = 'https://color-and-light.sentry.io/explore/logs/';
const SENTRY_PROJECT_ID = '4509029560418304';
const SENTRY_STATS_PERIOD = '30d';

// Sentry encodes a search filter's operator (e.g. "Contains") as a chip using
// this private-use unicode sentinel on either side of the operator name.
const OPERATOR = '\uf00d';

/** `field:⟪Contains⟫value` — substring match chip (used for the serial tag). */
export const sentryContainsQuery = (field: string, value: string): string =>
  `${field}:${OPERATOR}Contains${OPERATOR}${value}`;

/** `field:value` — exact match (used for built-in user context like user.id). */
export const sentryExactQuery = (field: string, value: string): string =>
  `${field}:${value}`;

const buildIssuesUrl = (query: string, environment?: string): string => {
  const params = new URLSearchParams({
    project: SENTRY_PROJECT_ID,
    referrer: 'issue-list',
    statsPeriod: SENTRY_STATS_PERIOD,
  });
  if (query) {
    params.set('query', query);
  }
  if (environment) {
    params.set('environment', environment);
  }
  return `${SENTRY_ISSUES_URL}?${params.toString()}`;
};

const buildLogsUrl = (
  logsQuery: string,
  logsFields: string[],
  environment?: string,
): string => {
  const params = new URLSearchParams({
    logsSortBys: '-timestamp',
    project: SENTRY_PROJECT_ID,
    statsPeriod: SENTRY_STATS_PERIOD,
  });
  if (logsQuery) {
    params.set('logsQuery', logsQuery);
  }
  if (environment) {
    params.set('environment', environment);
  }
  logsFields.forEach((field) => params.append('logsFields', field));
  return `${SENTRY_LOGS_URL}?${params.toString()}`;
};

interface SentryDropdownProps {
  /** Sentry issue-search query, e.g. built via sentryContainsQuery/sentryExactQuery. */
  issuesQuery?: string;
  /** Sentry logs query; defaults to issuesQuery when omitted. */
  logsQuery?: string;
  /** Columns shown in the logs view. */
  logsFields?: string[];
  /** Optional Sentry environment filter (e.g. 'development', 'beta'). */
  environment?: string;
  /** Extra classes for the btn-group wrapper (spacing, etc.). */
  className?: string;
  /** Menu alignment; defaults to 'end'. */
  menuAlign?: 'start' | 'end';
  title?: string;
}

const SentryDropdown: React.FC<SentryDropdownProps> = ({
  issuesQuery = '',
  logsQuery,
  logsFields = ['timestamp', 'message'],
  environment,
  className = 'me-2',
  menuAlign = 'end',
  title = 'Open Sentry',
}) => (
  <div className={`btn-group ${className}`.trim()}>
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary dropdown-toggle sentry-dropdown-toggle"
      data-bs-toggle="dropdown"
      aria-expanded="false"
      title={title}
    >
      <img
        className="sentry-dropdown-logo"
        src="/sentry-logo.png"
        alt="Sentry"
        style={{ width: '1rem', height: '1rem', objectFit: 'contain' }}
      />
    </button>
    <ul className={`dropdown-menu dropdown-menu-${menuAlign}`}>
      <li>
        <a
          className="dropdown-item d-flex align-items-center justify-content-between gap-3"
          href={buildIssuesUrl(issuesQuery, environment)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>Open Issues</span>
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-muted opacity-75" />
        </a>
      </li>
      <li>
        <a
          className="dropdown-item d-flex align-items-center justify-content-between gap-3"
          href={buildLogsUrl(logsQuery ?? issuesQuery, logsFields, environment)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span>Open Logs</span>
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-muted opacity-75" />
        </a>
      </li>
    </ul>
  </div>
);

export default SentryDropdown;
